import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

// Formats a number with thousands separators, e.g. 1940000 -> "1,940,000".
const formatNumber = value => (value || value === 0 ? Number(value).toLocaleString('en-US') : 0);

// prizesWon is 0 on some dashboards where nothing has ever been claimed — guard the
// division rather than let it come back NaN/Infinity.
const formatConversionPct = (claimed, won) => {
  if (!won) return '0.0';
  return ((claimed / won) * 100).toFixed(1);
};

// Same "updated N minutes ago" idiom as LoyaltyRankingTable's freshnessLabel:
// a falsy generatedAt means getLoyaltyDashboard computed these live (snapshot
// missing/stale), not that the figures are somehow untimed.
const freshnessLabel = (generatedAt) => {
  if (!generatedAt) return 'live';
  const parsed = moment(Number(generatedAt) || generatedAt);
  return parsed.isValid() ? `Figures updated ${parsed.fromNow()}` : 'live';
};

const LoyaltyKpis = ({ kpis }) => {
  const {
    fansWithPoints,
    totalPointsAwarded,
    prizesWon,
    prizesClaimed,
    prizesOpen,
    prizesNoCodes,
    achievementsUnlocked,
    codesTotal,
    codesUsed,
    lowStockPrizes,
    generatedAt,
  } = kpis;

  const codeStockPct = codesTotal ? Math.round((codesUsed / codesTotal) * 100) : 0;
  const claimedConversionPct = formatConversionPct(prizesClaimed, prizesWon);
  const hasCodeWarning = lowStockPrizes > 0 || prizesNoCodes > 0;

  return (
    <div style={{ marginBottom: 14 }}>
      <div className="stats">
        <div className="stat">
          <div className="k">Fans w/ points</div>
          <div className="v">{formatNumber(fansWithPoints)}</div>
        </div>
        <div className="stat">
          <div className="k">Points awarded</div>
          <div className="v">{formatNumber(totalPointsAwarded)}</div>
        </div>
        <div className="stat">
          <div className="k">Prizes won</div>
          <div className="v">{formatNumber(prizesWon)}</div>
          <div className="d">
            {formatNumber(prizesOpen)}
            {' open'}
          </div>
        </div>
        <div className="stat">
          <div className="k">Prizes claimed</div>
          <div className="v">{formatNumber(prizesClaimed)}</div>
          <div className="d">
            {claimedConversionPct}
            {'% of wins'}
          </div>
        </div>
        <div className="stat">
          <div className="k">Achievements</div>
          <div className="v">{formatNumber(achievementsUnlocked)}</div>
        </div>
        <div className={`stat${hasCodeWarning ? ' warn' : ''}`}>
          <div className="k">Code stock</div>
          <div className="v">
            {codeStockPct}
            %
          </div>
          {lowStockPrizes > 0 && (
            <div className="d">
              used ·
              {' '}
              <span className="dn">
                {lowStockPrizes}
                {' '}
                low
              </span>
            </div>
          )}
          {prizesNoCodes > 0 && (
            <div className="d">
              <span className="dn">
                {prizesNoCodes}
                {' prizes have no codes'}
              </span>
            </div>
          )}
          <div className="rulebar"><i style={{ width: `${codeStockPct}%` }} /></div>
        </div>
      </div>
      <div className="kpis-asof">{freshnessLabel(generatedAt)}</div>
    </div>
  );
};

LoyaltyKpis.propTypes = {
  kpis: PropTypes.shape({
    fansWithPoints: PropTypes.number,
    totalPointsAwarded: PropTypes.number,
    prizesWon: PropTypes.number,
    prizesClaimed: PropTypes.number,
    prizesOpen: PropTypes.number,
    prizesNoCodes: PropTypes.number,
    achievementsUnlocked: PropTypes.number,
    codesTotal: PropTypes.number,
    codesUsed: PropTypes.number,
    codesRemaining: PropTypes.number,
    lowStockPrizes: PropTypes.number,
    generatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

export default LoyaltyKpis;
